import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, ChevronUp, ChevronDown, Save, Star, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Loading } from "@/components/common/Loading";
import { RichTextEditor } from "@/components/forms/RichTextEditor";
import { AdminRefreshButton } from "@/components/admin/AdminRefreshButton";
import { FounderIcon, FOUNDER_ICON_NAMES } from "@/components/pricing/founderIcons";

const STATUS_OPTIONS = [
  { value: "included", label: "Included ✓" },
  { value: "not_included", label: "Not included ✗" },
  { value: "addon", label: "Add-on $" },
];
const GRANT_OPTIONS = [
  { value: "pro", label: "PRO" },
  { value: "premium", label: "CAPITAL" },
];

const move = (arr, i, dir) => {
  const j = i + dir;
  if (j < 0 || j >= arr.length) return arr;
  const copy = [...arr];
  [copy[i], copy[j]] = [copy[j], copy[i]];
  return copy;
};
const num = (v) => (v === "" || v === null || v === undefined ? "" : Number(v));

function FounderTierEditor({ tier, i, count, accountTags, onUpdate, onMove, onRemove }) {
  const setF = (j, patch) => onUpdate(i, { features: tier.features.map((f, fj) => (fj === j ? { ...f, ...patch } : f)) });
  const addFeat = () => onUpdate(i, { features: [...(tier.features || []), { name: "", status: "included", label: "", visible: true }] });
  const delFeat = (j) => onUpdate(i, { features: tier.features.filter((_, fj) => fj !== j) });
  const moveFeat = (j, dir) => onUpdate(i, { features: move(tier.features, j, dir) });

  return (
    <Card className="p-5" data-testid={`founder-tier-card-${i}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="text-sm font-[680] text-foreground">{tier.name || `Tier ${i + 1}`}</span>
          {tier.highlighted && <Star className="h-4 w-4 fill-amber-400 text-amber-400" />}
          {!tier.visible && <span className="rounded-full bg-secondary px-2 py-0.5 text-[11px] text-muted-foreground">Hidden</span>}
        </div>
        <div className="flex items-center gap-1">
          <Button size="icon" variant="ghost" className="h-7 w-7" disabled={i === 0} onClick={() => onMove(i, -1)} data-testid={`founder-up-${i}`}><ChevronUp className="h-4 w-4" /></Button>
          <Button size="icon" variant="ghost" className="h-7 w-7" disabled={i === count - 1} onClick={() => onMove(i, 1)} data-testid={`founder-down-${i}`}><ChevronDown className="h-4 w-4" /></Button>
          <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={() => onRemove(i)} data-testid={`founder-delete-${i}`}><Trash2 className="h-4 w-4" /></Button>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="space-y-1.5"><Label>Tier name</Label><Input value={tier.name} onChange={(e) => onUpdate(i, { name: e.target.value })} data-testid={`founder-name-${i}`} /></div>
        <div className="space-y-1.5"><Label>Tagline</Label><Input value={tier.tagline || ""} onChange={(e) => onUpdate(i, { tagline: e.target.value })} placeholder="Short line under the name" data-testid={`founder-tagline-${i}`} /></div>
      </div>

      <Separator className="my-4" />
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div className="space-y-1.5"><Label>Price ($, one-time)</Label><Input type="number" min="0" value={num(tier.price)} onChange={(e) => onUpdate(i, { price: e.target.value === "" ? 0 : Number(e.target.value) })} data-testid={`founder-price-${i}`} /></div>
        <div className="space-y-1.5"><Label>Quantity available (internal)</Label><Input type="number" min="0" value={num(tier.available)} onChange={(e) => onUpdate(i, { available: e.target.value === "" ? 0 : Number(e.target.value) })} data-testid={`founder-available-${i}`} /></div>
        <div className="space-y-1.5">
          <Label>Card icon</Label>
          <Select value={tier.icon || "Sparkles"} onValueChange={(v) => onUpdate(i, { icon: v })}>
            <SelectTrigger data-testid={`founder-icon-${i}`}>
              <span className="flex items-center gap-2"><FounderIcon name={tier.icon} className="h-4 w-4" /><SelectValue /></span>
            </SelectTrigger>
            <SelectContent className="max-h-72">
              {FOUNDER_ICON_NAMES.map((n) => (
                <SelectItem key={n} value={n}><span className="flex items-center gap-2"><FounderIcon name={n} className="h-4 w-4" />{n}</span></SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <Separator className="my-4" />
      <p className="text-xs font-[640] uppercase tracking-wide text-muted-foreground">On purchase, the buyer is granted:</p>
      <div className="mt-2 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <div className="space-y-1.5">
          <Label>Account tag</Label>
          <Select value={tier.account_tag_key || ""} onValueChange={(v) => onUpdate(i, { account_tag_key: v })}>
            <SelectTrigger data-testid={`founder-tag-${i}`}><SelectValue placeholder="Select tag" /></SelectTrigger>
            <SelectContent className="max-h-72">
              {(accountTags || []).map((t) => <SelectItem key={t.key} value={t.key}>{t.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label>Access tier</Label>
          <Select value={tier.grant_tier || "pro"} onValueChange={(v) => onUpdate(i, { grant_tier: v })}>
            <SelectTrigger data-testid={`founder-grant-tier-${i}`}><SelectValue /></SelectTrigger>
            <SelectContent>{GRANT_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5"><Label>Seats (licenses)</Label><Input type="number" min="1" value={num(tier.grant_seats)} onChange={(e) => onUpdate(i, { grant_seats: e.target.value === "" ? 1 : Number(e.target.value) })} data-testid={`founder-seats-${i}`} /></div>
        <div className="space-y-1.5"><Label>Duration (days, blank = life)</Label><Input type="number" min="0" value={tier.duration_days ?? ""} onChange={(e) => onUpdate(i, { duration_days: e.target.value === "" ? null : Number(e.target.value) })} data-testid={`founder-duration-days-${i}`} /></div>
      </div>
      <div className="mt-3 space-y-1.5"><Label>Duration label (shown on card, e.g. &ldquo;1 Year&rdquo;, &ldquo;For Life&rdquo;)</Label><Input value={tier.duration_label || ""} onChange={(e) => onUpdate(i, { duration_label: e.target.value })} className="sm:w-72" data-testid={`founder-duration-label-${i}`} /></div>

      <Separator className="my-4" />
      <div className="flex flex-wrap items-center gap-6">
        <label className="flex items-center gap-2 text-sm"><Switch checked={!!tier.visible} onCheckedChange={(v) => onUpdate(i, { visible: v })} data-testid={`founder-visible-${i}`} /> Show this card</label>
        <label className="flex items-center gap-2 text-sm"><Switch checked={!!tier.highlighted} onCheckedChange={(v) => onUpdate(i, { highlighted: v })} data-testid={`founder-highlighted-${i}`} /> Featured hero (full-width, top)</label>
      </div>

      <Separator className="my-4" />
      <div className="flex items-center justify-between">
        <Label>Features (shown on the card)</Label>
        <Button size="sm" variant="secondary" onClick={addFeat} data-testid={`founder-add-feature-${i}`}><Plus className="mr-1 h-3.5 w-3.5" /> Add feature</Button>
      </div>
      <div className="mt-2 space-y-2">
        {(tier.features || []).map((f, j) => (
          <div key={j} className="flex flex-wrap items-center gap-2 rounded-lg border border-border/70 p-2" data-testid={`founder-feature-${i}-${j}`}>
            <Input className="h-8 min-w-[200px] flex-1" value={f.name} onChange={(e) => setF(j, { name: e.target.value })} placeholder="Feature text" data-testid={`founder-feature-name-${i}-${j}`} />
            <div className="w-40">
              <Select value={f.status || "included"} onValueChange={(v) => setF(j, { status: v })}>
                <SelectTrigger className="h-8" data-testid={`founder-feature-status-${i}-${j}`}><SelectValue /></SelectTrigger>
                <SelectContent>{STATUS_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <label className="flex items-center gap-1 text-xs text-muted-foreground"><Switch checked={f.visible !== false} onCheckedChange={(v) => setF(j, { visible: v })} data-testid={`founder-feature-visible-${i}-${j}`} /> Show</label>
            <Button size="icon" variant="ghost" className="h-7 w-7" disabled={j === 0} onClick={() => moveFeat(j, -1)}><ChevronUp className="h-4 w-4" /></Button>
            <Button size="icon" variant="ghost" className="h-7 w-7" disabled={j === tier.features.length - 1} onClick={() => moveFeat(j, 1)}><ChevronDown className="h-4 w-4" /></Button>
            <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={() => delFeat(j)} data-testid={`founder-feature-delete-${i}-${j}`}><Trash2 className="h-4 w-4" /></Button>
          </div>
        ))}
        {(tier.features || []).length === 0 && <p className="text-sm text-muted-foreground">No features yet.</p>}
      </div>

      <Separator className="my-4" />
      <Label>Footnote (rich text, bottom of card)</Label>
      <div className="mt-2"><RichTextEditor value={tier.footnote} onChange={(v) => onUpdate(i, { footnote: v })} placeholder="Disclaimers (e.g. *for-life note)…" testId={`founder-footnote-${i}`} /></div>
    </Card>
  );
}

export default function AdminFounders() {
  const [tiers, setTiers] = useState(null);
  const [settings, setSettings] = useState({ title: "", intro: "", enabled: true, hide_after: null, audience: "everyone", audience_emails: [] });
  const [accountTags, setAccountTags] = useState([]);
  const [saving, setSaving] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/founders");
      setTiers(data.tiers || []);
      setSettings(data.settings || { title: "", intro: "" });
      setAccountTags(data.account_tags || []);
    } catch {
      setTiers((prev) => prev || []);
      toast.error("Could not load founders config.");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const updateTier = (i, patch) => setTiers((arr) => arr.map((t, idx) => (idx === i ? { ...t, ...patch } : t)));
  const moveTier = (i, dir) => setTiers((arr) => move(arr, i, dir));
  const addTier = () => setTiers((arr) => [...arr, {
    name: "New founder tier", tagline: "", price: 0, currency: "usd", available: 0, icon: "Sparkles",
    account_tag_key: "", grant_tier: "pro", grant_seats: 1, duration_label: "", duration_days: null,
    highlighted: false, visible: true, features: [], footnote: "",
  }]);
  const removeTier = (i) => { setTiers((arr) => arr.filter((_, idx) => idx !== i)); setConfirmDel(null); };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/founders", { settings, tiers });
      setTiers(data.tiers || []);
      setSettings(data.settings || settings);
      setAccountTags(data.account_tags || accountTags);
      toast.success("Founders saved — live on /pricing.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save.");
    } finally { setSaving(false); }
  };

  if (tiers === null) return <Loading />;

  return (
    <div data-testid="admin-founders">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-[680] text-foreground">Founders Membership</h2>
          <p className="text-sm text-muted-foreground">One-time Founder tiers shown below the plans on <span className="font-[560]">/pricing</span>. Each grants an account tag + tier on purchase.</p>
        </div>
        <div className="flex items-center gap-2">
          <AdminRefreshButton testId="admin-refresh-founders" onRefresh={load} />
          <Button variant="secondary" onClick={addTier} data-testid="founder-add-tier"><Plus className="mr-1.5 h-4 w-4" /> Add tier</Button>
          <Button onClick={save} disabled={saving} data-testid="founder-save-button">
            {saving ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Save className="mr-1.5 h-4 w-4" />} Save changes
          </Button>
        </div>
      </div>

      <Card className="mt-6 p-5" data-testid="founder-settings-editor">
        <h3 className="text-base font-[680] text-foreground">Section header</h3>
        <div className="mt-3 space-y-3">
          <div className="space-y-1.5"><Label>Title</Label><Input value={settings.title || ""} onChange={(e) => setSettings((s) => ({ ...s, title: e.target.value }))} data-testid="founder-section-title" /></div>
          <div className="space-y-1.5"><Label>Intro paragraph</Label><Textarea rows={3} value={settings.intro || ""} onChange={(e) => setSettings((s) => ({ ...s, intro: e.target.value }))} data-testid="founder-section-intro" /></div>
        </div>

        <Separator className="my-5" />
        <h3 className="text-base font-[680] text-foreground">Visibility</h3>
        <p className="text-sm text-muted-foreground">Founder tiers are temporary — control when and to whom they appear on <span className="font-[560]">/pricing</span> and <span className="font-[560]">/dashboard/plans</span>.</p>
        <div className="mt-4 space-y-4">
          <label className="flex items-center gap-3 text-sm">
            <Switch checked={settings.enabled !== false} onCheckedChange={(v) => setSettings((s) => ({ ...s, enabled: v }))} data-testid="founder-enabled-toggle" />
            <span><span className="font-[560] text-foreground">Show the Founders section</span> — turn the whole section on or off.</span>
          </label>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Auto-hide after</Label>
              <div className="flex items-center gap-2">
                <Input
                  type="datetime-local"
                  value={(settings.hide_after || "").slice(0, 16)}
                  onChange={(e) => setSettings((s) => ({ ...s, hide_after: e.target.value || null }))}
                  data-testid="founder-hide-after"
                />
                {settings.hide_after && (
                  <Button size="sm" variant="ghost" onClick={() => setSettings((s) => ({ ...s, hide_after: null }))} data-testid="founder-hide-after-clear">Clear</Button>
                )}
              </div>
              <p className="text-xs text-muted-foreground">Leave blank to keep it up indefinitely. After this time the section hides automatically.</p>
            </div>

            <div className="space-y-1.5">
              <Label>Audience</Label>
              <Select value={settings.audience || "everyone"} onValueChange={(v) => setSettings((s) => ({ ...s, audience: v }))}>
                <SelectTrigger data-testid="founder-audience"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="everyone">Everyone</SelectItem>
                  <SelectItem value="specific">Only specific users (by email)</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">&ldquo;Everyone&rdquo; shows to all visitors; &ldquo;specific&rdquo; shows only to the logged-in emails below.</p>
            </div>
          </div>

          {settings.audience === "specific" && (
            <div className="space-y-1.5">
              <Label>Allowed emails</Label>
              <Textarea
                rows={3}
                value={(settings.audience_emails || []).join("\n")}
                onChange={(e) => setSettings((s) => ({ ...s, audience_emails: e.target.value.split(/[\n,]/).map((x) => x.trim()).filter(Boolean) }))}
                placeholder={"one@example.com\ntwo@example.com"}
                data-testid="founder-audience-emails"
              />
              <p className="text-xs text-muted-foreground">One email per line (or comma-separated). Only these signed-in users will see the Founders section.</p>
            </div>
          )}
        </div>
      </Card>

      <div className="mt-6 space-y-5">
        {tiers.map((t, i) => (
          <FounderTierEditor key={i} tier={t} i={i} count={tiers.length} accountTags={accountTags} onUpdate={updateTier} onMove={moveTier} onRemove={(idx) => setConfirmDel(idx)} />
        ))}
        {tiers.length === 0 && <p className="text-sm text-muted-foreground">No founder tiers. Click &ldquo;Add tier&rdquo; to create one.</p>}
      </div>

      <div className="sticky bottom-4 mt-8 flex justify-end">
        <Button onClick={save} disabled={saving} size="lg" className="shadow-lg" data-testid="founder-save-button-bottom">
          {saving ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Save className="mr-1.5 h-4 w-4" />} Save changes
        </Button>
      </div>

      <AlertDialog open={confirmDel !== null} onOpenChange={(o) => !o && setConfirmDel(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this founder tier?</AlertDialogTitle>
            <AlertDialogDescription>It will be removed from the editor. Click &ldquo;Save changes&rdquo; afterwards to update the live page.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => removeTier(confirmDel)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="founder-delete-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
